import type {
  DataTableColumn,
  DataTableColumnApiId,
  DataTableColumnMoveRequest,
  DataTableType,
} from '@/components/UI/DataTableV2/DataTable.model';
import ReactSelect, { type SingleValue } from 'react-select';
import { isChildColumn } from '@/components/UI/DataTableV2/DataTable.util';
import { ColumnName } from '@/components/UI/DataTableV2/headers/ColumnName/ColumnName';
import { ModalActions, ModalContainer, ModalHeader } from '@/utils/modal';
import { ErrorMessage } from '@hookform/error-message';
import { type FC, useCallback, useMemo } from 'react';
import { Controller, FormProvider, useForm } from 'react-hook-form-latest';
import Radio from '@/components/UI/FormElements/Radio';

interface MoveDatatableColumnProps {
  tableType: DataTableType;
  column: DataTableColumn;
  columns: Array<DataTableColumn>;
  closeModal: () => void;
  onSave: (columnUpdate: DataTableColumnMoveRequest) => Promise<void>;
}

/**
 * Filters columns to only valid move targets based on parent/child relationships.
 *
 * @param columns - All available columns in the table
 * @param columnToMove - The column being moved
 * @returns Map of valid target columns, excluding the column being moved
 *
 * @remarks
 * Child columns can only be moved within their parent group (siblings only).
 * Regular and parent columns can be moved to any regular or parent position.
 * Child columns from other groups are never valid targets.
 */
const getValidMoveTargets = (
  columns: Array<DataTableColumn>,
  columnToMove: DataTableColumn
): Map<DataTableColumnApiId, DataTableColumn> => {
  const isMovingChildColumn = isChildColumn(columnToMove);
  const parentId = isMovingChildColumn ? columnToMove.parent_api_id : null;

  const parentColumnIds = new Set(
    columns.reduce<Array<DataTableColumnApiId>>((acc, col) => {
      if (isChildColumn(col)) {
        acc.push(col.parent_api_id);
      }
      return acc;
    }, [])
  );

  return columns.reduce<Map<DataTableColumnApiId, DataTableColumn>>((acc, col) => {
    if (col.id === columnToMove.id) {
      return acc;
    }

    const isTargetParent = parentColumnIds.has(col.id);
    const isTargetChild = isChildColumn(col);

    if (isMovingChildColumn) {
      if (isTargetParent || !isTargetChild || col.parent_api_id !== parentId) {
        return acc;
      }
    } else {
      if (isTargetChild) {
        return acc;
      }
    }
    return acc.set(col.id, col);
  }, new Map<DataTableColumnApiId, DataTableColumn>());
};

/**
 * Modal for moving a datatable column to a new position relative to another column.
 *
 * @param tableType - The type of datatable (time or custom)
 * @param column - The column being moved
 * @param columns - All available columns in the datatable
 * @param closeModal - Callback to close the modal
 * @param onSave - Callback to save the column move operation
 *
 * @remarks
 * Enforces column hierarchy rules: child columns can only move within their parent group,
 * while regular and parent columns can move freely among non-child positions.
 * The modal closes immediately upon submission without waiting for the save operation.
 */
export const MoveDatatableColumn: FC<MoveDatatableColumnProps> = ({ column, columns, onSave, closeModal }) => {
  const onSubmit = async (update: DataTableColumnMoveRequest) => {
    closeModal();
    await onSave({ column_id: column.id, target_column_id: update.target_column_id, position: update.position });
  };

  const formMethods = useForm<DataTableColumnMoveRequest>({
    mode: 'onChange',
    defaultValues: { position: 'before' },
  });

  const {
    handleSubmit,
    formState: { errors, isSubmitting, isValid, isDirty },
  } = formMethods;

  const filteredColumns: Map<DataTableColumnApiId, DataTableColumn> = useMemo(
    () => getValidMoveTargets(columns, column),
    [columns, column]
  );

  const columnsById: Map<DataTableColumnApiId, DataTableColumn> = useMemo(
    () => new Map(columns.map((col) => [col.id, col])),
    [columns]
  );

  const formatOptionLabel = useCallback(
    (option: DataTableColumn) => <ColumnName column={option} columnsById={columnsById} />,
    [columnsById]
  );

  return (
    <ModalContainer size="narrow">
      <ModalHeader
        title="Move column"
        subText={<ColumnName column={column} columnsById={columnsById} />}
        closeModal={closeModal}
        className="pa3 bb b--moon-gray bg-white"
      />
      <FormProvider {...formMethods}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="ph3 pv2">
            <Controller
              control={formMethods.control}
              name={'position'}
              render={({ field }) => (
                <>
                  <Radio
                    id="before"
                    className="pb2"
                    label="Before"
                    value="before"
                    checked={field.value === 'before'}
                    onChange={() => field.onChange('before')}
                  />
                  <Radio
                    id="after"
                    label="After"
                    value="after"
                    checked={field.value === 'after'}
                    onChange={() => field.onChange('after')}
                  />
                </>
              )}
            />
            <ErrorMessage
              errors={errors}
              name="position"
              render={({ message }) => <small className="red db pt2">{message}</small>}
            />
          </div>
          <div className="ph3 pv2">
            <label className="db fw6 f6 mb2">
              Column <span className="red">*</span>
            </label>
            <Controller
              control={formMethods.control}
              name={'target_column_id'}
              rules={{ required: 'Column is required' }}
              render={({ field }) => (
                <ReactSelect<DataTableColumn>
                  value={filteredColumns.get(field.value)}
                  options={Array.from(filteredColumns.values())}
                  onChange={(option: SingleValue<DataTableColumn>) => field.onChange(option?.id)}
                  isOptionSelected={(option) => option.id === field.value}
                  formatOptionLabel={formatOptionLabel}
                  placeholder="Select a column..."
                  className="w5 ui__select f6"
                  classNamePrefix="ui__select"
                  styles={{
                    control: (base) => ({
                      ...base,
                      height: 40,
                      minHeight: 40,
                    }),
                  }}
                />
              )}
            />
            <ErrorMessage
              errors={errors}
              name="target_column_id"
              render={({ message }) => <small className="red db pt2">{message}</small>}
            />
          </div>
        </form>
      </FormProvider>
      <ModalActions
        className="pa3 mt3 bt b--moon-gray"
        onSubmit={handleSubmit(onSubmit)}
        onCancel={closeModal}
        submitButtonProps={{ disabled: !isValid || !isDirty || isSubmitting }}
      />
    </ModalContainer>
  );
};
